import {
  type ChartPlanInput,
  type ChartPlanResult,
  type FileDownloadUrl,
  type FileTypeGroup,
  type DiagramGenerateInput,
  type DiagramGenerateResult,
  type SectionWriteInput,
  type SectionWriteResult,
  type SlideGenerateInput,
  type SlideGenerateResult,
  type FormatDocumentInput,
  type FormatDocumentResult,
  type MyFileItem,
  type MyFileSource,
  type MyFilesPage,
  type MyFileUrlsResult,
  type MyFileVersions,
  type PastePlanInput,
  type PastePlanResult,
  MY_FILES_PAGE_MAX,
  type ImageDescribeInput,
  type ImageDescribeResult,
  type ImageDescribeUpload,
  type ImageDescribeUploadInput,
  type ConvertAiInput,
  type ConvertAiPlanResult,
  type ImportAiInput,
  type ImportAiPlanResult,
  type ImageSubjectInput,
  type ImageSubjectResult,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/** Bộ lọc của danh sách — đúng các trường của `MyFilesQuery`, trừ con trỏ. */
export interface MyFilesFilter {
  readonly source?: MyFileSource;
  readonly q?: string;
  readonly type?: FileTypeGroup;
  /** Thư mục tự tạo (APM-MYF-j): uuid, hoặc `'root'` = tệp "của tôi" ngoài mọi thư mục. */
  readonly folderId?: string;
  readonly sort: 'name' | 'createdAt' | 'size';
  readonly dir: 'asc' | 'desc';
}

/** Một lượt đọc lấy nhiều nhất bao nhiêu mục — trần của API (`MY_FILES_PAGE_MAX`). */
export const MY_FILES_LIMIT = 100;

/** Trang "Tệp" — APM-MYF-c, docs/plan-my-files.md §3.3. */
export const myFilesApi = {
  list: (filter: MyFilesFilter, cursor?: string) => {
    const params = new URLSearchParams({
      sort: filter.sort,
      dir: filter.dir,
      limit: String(MY_FILES_LIMIT),
    });
    if (filter.source) params.set('source', filter.source);
    if (filter.q) params.set('q', filter.q);
    if (filter.type) params.set('type', filter.type);
    if (filter.folderId) params.set('folderId', filter.folderId);
    if (cursor) params.set('cursor', cursor);
    return apiFetch<MyFilesPage>(`/me/files?${params.toString()}`);
  },

  /** URL xem cho nhiều file một lần (ảnh/video trong lưới). Tối đa 100 id. */
  urls: (ids: readonly string[]) =>
    apiFetch<MyFileUrlsResult>('/me/files/urls', { method: 'POST', body: { ids } }),

  /**
   * Như `urls` nhưng không giới hạn số id: chia lô 100 (trần của API). Cho ảnh
   * `apm-file:` trong tài liệu — khung xem trước (`-d`) và hộp soạn (`-f`).
   */
  urlsOf: async (ids: readonly string[]): Promise<Record<string, string | null>> => {
    const out: Record<string, string | null> = {};
    for (let start = 0; start < ids.length; start += MY_FILES_PAGE_MAX) {
      const result = await myFilesApi.urls(ids.slice(start, start + MY_FILES_PAGE_MAX));
      Object.assign(out, result.urls);
    }
    return out;
  },

  /** Presigned GET — file không bao giờ đi qua Node (luật #3). */
  downloadUrl: (id: string) => apiFetch<FileDownloadUrl>(`/me/files/${id}/url`),

  /** Một dòng của trang theo id — link `/files?preview=<id>` (APM-PVLINK). */
  item: (id: string) => apiFetch<MyFileItem>(`/me/files/${id}/item`),

  remove: (id: string) => apiFetch<void>(`/me/files/${id}`, { method: 'DELETE' }),

  /** Đổi tên tệp cá nhân — tên đầy đủ, đuôi giữ nguyên; cả nhóm phiên bản đổi theo (APM-VER-c). */
  rename: (id: string, name: string) =>
    apiFetch<MyFileItem>(`/me/files/${id}`, { method: 'PATCH', body: { name } }),

  /** "Các phiên bản" của một tài liệu cá nhân, theo thứ tự người dùng xếp — APM-MYF-n / APM-VER-a. */
  versions: (id: string) => apiFetch<MyFileVersions>(`/me/files/${id}/versions`),

  /** Xếp lại phiên bản (kéo thả) — `ids` là toàn bộ nhóm; bản đầu thành đại diện (APM-VER-a). */
  reorderVersions: (id: string, ids: readonly string[]) =>
    apiFetch<MyFileVersions>(`/me/files/${id}/versions/order`, {
      method: 'PUT',
      body: { ids: [...ids] },
    }),

  /** Xoá đúng một phiên bản, kể cả bản đại diện — bản kế tiếp lên thay (APM-VER-a). */
  removeVersion: (id: string) => apiFetch<void>(`/me/files/${id}/version`, { method: 'DELETE' }),

  /** Kế hoạch định dạng do skill `format_document` đề xuất (APM-CD-g/-h) — chưa áp gì. */
  formatPlan: (input: FormatDocumentInput) =>
    apiFetch<FormatDocumentResult>('/me/files/format-plan', { method: 'POST', body: input }),

  /** Nội dung sơ đồ do skill `generate_diagram` đề xuất (APM-DRW-g/-h) — chưa chèn gì. */
  diagramPlan: (input: DiagramGenerateInput) =>
    apiFetch<DiagramGenerateResult>('/me/files/diagram-plan', { method: 'POST', body: input }),

  /** Slide đề xuất cho một vùng ngăn — skill `generate_slides` (APM-SLD-g/-h). Chỉ spec, chưa chèn gì. */
  slidePlan: (input: SlideGenerateInput) =>
    apiFetch<SlideGenerateResult>('/me/files/slide-plan', { method: 'POST', body: input }),

  /** Bản viết chi tiết một mục — skill `write_section` (APM-SWR-b). Client áp thành đề xuất, server không lưu. */
  sectionWrite: (input: SectionWriteInput) =>
    apiFetch<SectionWriteResult>('/me/files/section-write', { method: 'POST', body: input }),

  /** Xin URL PUT bản thu nhỏ của một ảnh chờ mô tả — APM-SLD-n. */
  imageDescribeUpload: (input: ImageDescribeUploadInput) =>
    apiFetch<ImageDescribeUpload>('/me/files/image-description/upload', {
      method: 'POST',
      body: input,
    }),

  /** Mô tả ảnh bằng vision — skill `describe_image` (APM-SLD-n). Kết quả về client, client ghi vào node ảnh. */
  imageDescribe: (input: ImageDescribeInput) =>
    apiFetch<ImageDescribeResult>('/me/files/image-description', { method: 'POST', body: input }),

  /**
   * AI chỉ chủ thể (hộp + điểm) cho hộp Xoá nền — skill `locate_subject` (APM-BGR-d). Bản thu nhỏ tải lên bằng
   * `imageDescribeUpload`. Kết quả chỉ là gợi ý: hộp hiện điểm trên ảnh, người dùng sửa rồi mới Áp dụng.
   */
  imageSubjectLocate: (input: ImageSubjectInput) =>
    apiFetch<ImageSubjectResult>('/me/files/image-background/locate', {
      method: 'POST',
      body: input,
    }),

  /** Biểu đồ đề xuất cho một bảng — skill `suggest_charts` (APM-CH-f/-g). Chỉ spec, chưa chèn gì. */
  chartPlan: (input: ChartPlanInput) =>
    apiFetch<ChartPlanResult>('/me/files/chart-plan', { method: 'POST', body: input }),

  /** Kế hoạch định dạng đoạn vừa dán do skill `format_paste` đề xuất (APM-SP-d/-e) — chưa áp gì. */
  pastePlan: (input: PastePlanInput) =>
    apiFetch<PastePlanResult>('/me/files/paste-plan', { method: 'POST', body: input }),

  /** "Nhập với AI" — skill `import_document` (APM-CDIMP-g): LaTeX cho vùng công thức + câu trả lời. */
  importPlan: (input: ImportAiInput) =>
    apiFetch<ImportAiPlanResult>('/me/files/import-plan', { method: 'POST', body: input }),
  /** "Dùng AI cho phần khó" của hộp chuyển `.docx` / `.xlsx` — APM-CVT-h. */
  convertPlan: (input: ConvertAiInput) =>
    apiFetch<ConvertAiPlanResult>('/me/files/convert-plan', { method: 'POST', body: input }),
};

export const myFilesKeys = {
  all: ['me', 'files'] as const,
  list: (filter: MyFilesFilter) => ['me', 'files', 'list', filter] as const,
  urls: (ids: readonly string[]) => ['me', 'files', 'urls', ids] as const,
  /** Presigned GET của một file — khung xem trước (APM-MYF-d). */
  url: (id: string) => ['me', 'files', 'url', id] as const,
  item: (id: string) => ['me', 'files', 'item', id] as const,
  /** Nội dung chữ đọc thẳng từ S3 theo URL đã ký — không qua API (luật #3). */
  content: (url: string) => ['me', 'files', 'content', url] as const,
  /** Các phiên bản của tài liệu chứa tệp `id` (APM-MYF-n). */
  versions: (id: string) => ['me', 'files', 'versions', id] as const,
};
